import { useEffect, useRef } from "react"
import { Chevron, DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { CaretDown, CaretUp } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface CalendarCustomProps {
  disabledMatcher?: (date: Date) => boolean
}

type CalendarProps = React.ComponentProps<typeof DayPicker> & CalendarCustomProps

export const CalendarFooter = ({ className, children, ...props }: React.ComponentPropsWithoutRef<"button">) => {
  const { t } = useTranslation()
  return (
    <div className={className}>
      <hr />
      <div className="p-2">
        {children}
        <Button data-testid="date-picker-reset" className="ml-auto" variant="tertiary" {...props}>
          {t("common.button.clear")}
        </Button>
      </div>
    </div>
  )
}

export const Calendar = (props: CalendarProps) => {
  const { className, classNames, showOutsideDays, components, today, disabledMatcher, ...rest } = props
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      today={today ?? new Date()}
      disabled={disabledMatcher}
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      navLayout="after"
      className={className}
      classNames={{
        months: cn("relative flex flex-col gap-4 px-3 py-2 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-2", defaultClassNames.month),
        nav: cn("absolute top-2 right-2 flex w-full items-center justify-end", defaultClassNames.nav),
        month_caption: cn("flex h-10 items-center justify-between", defaultClassNames.month_caption),
        button_previous: cn(
          "text-on-surface-subdued aria-disabled:text-on-surface-disabled hover:bg-surface-subdued cursor-pointer rounded-full p-2",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          "text-on-surface-subdued aria-disabled:text-on-surface-disabled hover:bg-surface-subdued cursor-pointer rounded-full p-2",
          defaultClassNames.button_next,
        ),
        month_grid: "w-full border-collapse",
        weekdays: cn("flex justify-around", defaultClassNames.weekdays),
        weekday: cn(
          "text-on-surface flex size-10 items-center justify-center rounded-md text-xs font-normal tracking-wide select-none",
          defaultClassNames.weekday,
        ),
        week: cn("flex w-full gap-1 not-last:mb-1", defaultClassNames.week),
        weeks: cn("js-sofia-rdp-tbody rdp-tbody", defaultClassNames.weeks),
        day: cn("hover:bg-transparent", defaultClassNames.day),
        day_button: cn("h-full w-full", defaultClassNames.day_button),
        ...classNames,
      }}
      data-testid="calendar-day-picker"
      components={{ Chevron: CalendarChevron, DayButton: CalendarDayButton, ...components }}
      footer
      {...rest}
    />
  )
}

const CalendarChevron = ({ orientation, ...props }: React.ComponentProps<typeof Chevron>) => {
  return orientation === "left" ? <CaretUp {...props} /> : <CaretDown {...props} />
}

const CalendarDayButton = ({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) => {
  const defaultClassNames = getDefaultClassNames()
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="tertiary"
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today && !modifiers.selected && !modifiers.range_start && !modifiers.range_end}
      data-selected-single={
        modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-rdp-in-selecting={
        modifiers["rdp-in-selecting"] && !modifiers.selected && !modifiers.range_start && !modifiers.range_end
      }
      className={cn(
        "text-on-surface data-[selected-single=true]:bg-primary data-[rdp-in-selecting=true]:rdp-in-selecting data-[today=true]:text-primary data-[today=true]:border-primary data-[selected-single=true]:text-on-primary data-[range-middle=true]:bg-primary-surface data-[range-middle=true]:rdp-day_range_middle data-[range-middle=true]:text-on-primary-surface data-[range-start=true]:rdp-day_range_start data-[range-start=true]:bg-primary data-[range-start=true]:text-on-primary data-[range-end=true]:rdp-day_range_end data-[range-end=true]:bg-primary data-[range-end=true]:text-on-primary group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex aspect-square size-10 min-w-(--cell-size) flex-col gap-1 rounded-md leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] disabled:bg-transparent data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[today=true]:border data-[today=true]:hover:bg-transparent",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}
